import '@app/App.css';
import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { Dashboard } from "@dto/Dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { BASE_URL } from "@utils/constants";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_DashboardMenuItem, TW_DashboardMenuItemIcon, TW_ErrorMessage, useHover } from "@utils/twClasses";
import { put } from "@utils/utils";
import { Edit2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";
import { z } from "zod";

interface DashboardMenuRenameItemProps {
  dashboard: Dashboard;
  onDashboardUpdate: (dashboard: Dashboard) => void;
  closeMenu: () => void;
}

function DashboardMenuRenameItem({ dashboard, onDashboardUpdate, closeMenu }: DashboardMenuRenameItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));

  const dashboardUpdated = (dashboard: Dashboard) => {
    setIsDialogOpen(false);
    closeMenu();
    onDashboardUpdate(dashboard);
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(false);
    closeMenu();
  }

  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(true);
  }

  function onOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      closeMenu();
    }
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger className={TW_DashboardMenuItem + ` ${dashboard === null ? "item-disabled" : ""}`} {...buttonHover} onClick={click}><Edit2 className={TW_DashboardMenuItemIcon} />{t("dashboard.button.rename.text")}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("dashboard.dialog.rename.title")}</DialogTitle>
          <DialogDescription className="hidden">{t("dashboard.dialog.rename.description")}</DialogDescription>
        </DialogHeader>
        {dashboard && <DashboardRenameForm dashboard={dashboard} initialDashboard={dashboard} onDashboardUpdate={dashboardUpdated} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface DashboardRenameFormProps {
  dashboard: Dashboard;
  initialDashboard: Dashboard;
  onDashboardUpdate: (dashboard: Dashboard) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function DashboardRenameForm({ dashboard, initialDashboard, onDashboardUpdate, onCancel }: DashboardRenameFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const [error, setError] = useState<string | null>(null);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const formSchema = z.object({
    dashboardName: z.string().min(1, {
      message: t("dashboard.dialog.error.name.missing"),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      dashboardName: initialDashboard.name,
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {

    if (!workspace?._key) {
      console.error("Workspace key is not set");
      return;
    }

    // No need to update the workspace if the name is the same
    if (values.dashboardName === initialDashboard.name) {
      return;
    }

    const updatedDashboard = {
      ...initialDashboard,
      name: values.dashboardName,
    }
    put<Dashboard>(`${BASE_URL}/model/workspaces/${workspace._key}/dashboards/${dashboard._key}`, updatedDashboard).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        }
      } else {
        setError(null);
        onDashboardUpdate(updatedDashboard);
      }
    })
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="dashboardName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("dashboard.dialog.rename.label")}</FormLabel>
              <FormControl>
                <Input placeholder={t("dashboard.dialog.rename.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default DashboardMenuRenameItem;
